import React, {useEffect, useState} from 'react';
import {Alert, Form, Input, ConfigProvider, Button, Space, Tag, Typography, message} from "antd";
import {green} from "@ant-design/colors";

import {CheckCircleOutlined, LoadingOutlined, MinusCircleOutlined, PlusOutlined} from "@ant-design/icons";
import dayjs from "dayjs";

import useMortgage from "../../../useMortgage";
import {performGQL} from "../../../../../api-functions/functions";
import {getClientValue} from "../_section/data.class";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);


const fragment = `
    id
    uploadID
    date
    balance
    balanceBoundary
    dirty
    createdAt
    _version
    _deleted
`
export const createUserMortgageUploadStatementBalance = /* GraphQL */ `
    mutation CreateUserMortgageUploadStatementBalance(
        $input: CreateUserMortgageUploadStatementBalanceInput!
        $condition: ModelUserMortgageUploadStatementBalanceConditionInput
    ) {
        createUserMortgageUploadStatementBalance(
            input: $input
            condition: $condition
        ) {
            ${fragment}
        }
    }
`;
export const updateUserMortgageUploadStatementBalance = /* GraphQL */ `
    mutation UpdateUserMortgageUploadStatementBalance(
        $input: UpdateUserMortgageUploadStatementBalanceInput!
        $condition: ModelUserMortgageUploadStatementBalanceConditionInput
    ) {
        updateUserMortgageUploadStatementBalance(
            input: $input
            condition: $condition
        ) {
            ${fragment}
        }
    }
`;

export const deleteUserMortgageUploadStatementBalance = /* GraphQL */ `
    mutation DeleteUserMortgageUploadStatementBalance(
        $input: DeleteUserMortgageUploadStatementBalanceInput!
        $condition: ModelUserMortgageUploadStatementBalanceConditionInput
    ) {
        deleteUserMortgageUploadStatementBalance(
            input: $input
            condition: $condition
        ) {
            ${fragment}
        }
    }
`;

// Same generic productAPI mutation apps/admin's own sussd-mono-app/api/
// productAPI.js dispatches every products-runtime-engine action through
// (see @sussd-mono/shared's own ui-resources/api-resolver.js remoteAPI) -
// called directly here rather than importing that app's own wrapper, since
// this file lives in the old app tree, not sussd-mono-app. action routes
// server-side (dispatchers/entry.js's own ROUTES table) to
// product7package7task7upload/commands/extract-statement-balances.js.
// payload/the response are both AWSJSON - stringify going in, JSON.parse
// coming out.
const productAPIMutation = /* GraphQL */ `
    mutation ProductAPI($action: String!, $payload: AWSJSON) {
        productAPI(action: $action, payload: $payload)
    }
`;

export const onPasteCurrencyHandler = (e) => {
    e.preventDefault();
    try {
        const pasted = (e.clipboardData || window.clipboardData).getData('text');
        const cleaned = pasted.replace(/[^\d.-]/g, '').replace(/(?!^)-/g, '');

        const input = e.target;
        const numericValue = parseFloat(cleaned);
        const formattedValue = isNaN(numericValue) ? '' : numericValue.toFixed(2);

        // Force-set value and trigger React onChange
        const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
            window.HTMLInputElement.prototype,
            'value'
        ).set;
        nativeInputValueSetter.call(input, formattedValue);
        input.dispatchEvent(new Event('input', { bubbles: true }));

        // Delay caret setting until after value is committed
        requestAnimationFrame(() => {
            try {
                const el = document.activeElement; // fresh reference
                if (el && el.tagName === 'INPUT') {
                    el.setSelectionRange(formattedValue.length, formattedValue.length);
                }
            } catch (err) {
                console.warn("Couldn't set caret:", err);
            }
        });
    } catch (err) {
        console.error("onPasteCurrencyHandler error:", err);
    }
};

function BalancesForm({uploadID}) {


    const [formChanged, setFormChanged] = useState(false);
    const [busy, setBusy] = useState(false);
    const [saved, setSaved] = useState(false);
    const [extracting, setExtracting] = useState(false);
    const mortgage = useMortgage();

    const upload = mortgage.uploads.find(u => u.id === uploadID);

    // processedAnalysis is a shared, multi-key AWSJSON object - one key per
    // named extraction this upload has run (extract-and-save-balances.js's
    // own "Balance Failure", a future extract-statement-summary.js's own
    // "Summary Failure", etc.) - see that file's own recordExtractionFailure/
    // parseProcessedAnalysis. Re-parsed on every render, so this reflects
    // MortgageProvider's own live subscription update the moment a failure
    // (or a clearing, on a successful re-run) lands, with no extra effect
    // needed here - it's a plain display, not a controlled form field the
    // way the balance rows themselves are.
    let processedAnalysisErrors = [];
    if (upload?.processedAnalysis) {
        try {
            const parsed = typeof upload.processedAnalysis === 'string' ? JSON.parse(upload.processedAnalysis) : upload.processedAnalysis;
            // processedAnalysis also holds the scan result ({statement: {...}}), so keep only text messages
            processedAnalysisErrors = Object.entries(parsed || {}).filter(([, message]) => typeof message === 'string');
        } catch (e) {
            console.error('Could not parse upload.processedAnalysis', e);
        }
    }
    const balancesSorter = (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime();
    const balanceNeededEnd = mortgage.requirement.statementAlignmentDate ? dayjs(mortgage.requirement.statementAlignmentDate, "YYYY-MM-DD").startOf('day') : null;
    const balanceNeededStart = balanceNeededEnd ? balanceNeededEnd.add(1,'day').startOf('day').subtract(6, "month") : null;

    const balances = (upload?.balances?.items || []).filter(b => !b._deleted).map(b => ({
        ...b,
        dayjs: dayjs(b.date, "YYYY-MM-DD")
    })).sort((a, b) => a.dayjs.isBefore(b.dayjs) ? -1 : 1);
    const getVerifiedData = (name)=>{
        const items = upload?.verifiedData?.items || []
        const item = items.find(it => it.name === name);
        if (item) {

            return getClientValue(item.verifiedValue, item.verifiedValueType)

        }
        return null
    }


    // OPENING = the balance the window/upload started from, before that
    // day's own movement. CLOSING = the balance as of the end of that day,
    // including it. Necessary now that a date can hold two distinct rows -
    // see compute-balances.js's own header comment for why "the balance on
    // this date" is genuinely ambiguous without this tag whenever a real
    // transaction lands exactly on a boundary date.
    const uploadStart = getVerifiedData('startDate')?.startOf('day') // || balances[0]?.date
    const uploadEnd = getVerifiedData('endDate') ?.startOf('day') // || balances[balances.length - 1]?.date
    const hasBalanceOn = (date, boundary) => upload.balances?.items?.find(b => b.date === date && b.balanceBoundary === boundary)?.balance
    const additionalInitEntries = []
    if (uploadStart){
        if (!hasBalanceOn(uploadStart.format("YYYY-MM-DD"), 'OPENING')) {
            additionalInitEntries.push({
                date: uploadStart.format("YYYY-MM-DD"),
                balanceBoundary: 'OPENING',
                balance: '',
            })
        }
    }
    if (uploadEnd) {
        if (!hasBalanceOn(uploadEnd.format("YYYY-MM-DD"), 'CLOSING')) {
            additionalInitEntries.push({
                date: uploadEnd.format("YYYY-MM-DD"),
                balanceBoundary: 'CLOSING',
                balance: '',
            })
        }
    }


    if (balanceNeededEnd) {
        // Does the statement fall within the current range limit from statementAlignmentDate?
        if (balanceNeededStart && uploadEnd && uploadStart && balanceNeededStart.isSameOrAfter(uploadStart) && balanceNeededStart.isSameOrBefore(uploadEnd)) {
            // Do we have a balance for this date?
            if (!hasBalanceOn(balanceNeededStart.format("YYYY-MM-DD"), 'OPENING')) {
                additionalInitEntries.push({
                    date: balanceNeededStart.format("YYYY-MM-DD"),
                    balanceBoundary: 'OPENING',
                    balance: '',
                })
            }
        }
        if (balanceNeededEnd && uploadEnd && uploadStart && balanceNeededEnd.isSameOrAfter(uploadStart) && balanceNeededEnd.isSameOrBefore(uploadEnd)) {
            // Do we have a balance for this date?
            if (!hasBalanceOn(balanceNeededEnd.format("YYYY-MM-DD"), 'CLOSING')) {
                additionalInitEntries.push({
                    date: balanceNeededEnd.format("YYYY-MM-DD"),
                    balanceBoundary: 'CLOSING',
                    balance: '',
                })
            }
        }
    }

    // Pre-existing gap: each push above only checks upload.balances.items
    // (already-SAVED balances) for that (date, boundary) pair, never the
    // entries this same block already pushed - so e.g. balanceNeededStart
    // landing on the same day AND boundary as uploadStart pushed two
    // identical placeholder rows. Collapsing by (date, boundary) here (keep
    // first) fixes it for every combination at once, rather than adding the
    // same "already in this list" guard to all 4 push sites above. Two
    // DIFFERENT boundaries sharing a date are never collapsed - they're
    // legitimately two separate rows, not a duplicate.
    const deduplicatedAdditionalInitEntries = additionalInitEntries.filter(
        (entry, index) => additionalInitEntries.findIndex(e => e.date === entry.date && e.balanceBoundary === entry.balanceBoundary) === index
    )

    const [form] = Form.useForm();
    const initialValues = {

        startDate: upload?.startDate || '',
        endDate: upload?.endDate || '',
        entries: upload?.balances?.items ? upload.balances.items.filter(it => !it._deleted).map(b => ({
            date: b.date,
            balance: b.balance,
            balanceBoundary: b.balanceBoundary || null,
            id: b.id
        })) : null
    }

    const onValuesChange = (changedValues, allValues) => {
        if (JSON.stringify(allValues) !== JSON.stringify(initialValues)) {
            if (!formChanged) {
                setFormChanged(true);
            }
        } else if (formChanged) {
            setFormChanged(false);
        }
        setSaved(false)

        const entries = allValues.entries || [];
        const additionalEntries = []
        if (allValues.endDate && !entries.find(b => b.date === allValues.endDate)) {
            additionalEntries.push({
                date: allValues.endDate,
                balance: '',
                id: null,
            })
        }
        if (allValues.startDate && !entries.find(b => b.date === allValues.startDate)) {
            additionalEntries.push({
                date: allValues.startDate,
                balance: '',
                id: null,
            })
        }
        if (additionalEntries.length > 0) {
            form.setFieldsValue({
                entries: [...entries, ...additionalEntries].sort(balancesSorter)
            });
        }
    }

    const addNewBalance = async (date, amount, balanceBoundary) => {
        const input = {
            uploadID,
            date,
            balance: Number(amount),
            ...(balanceBoundary ? {balanceBoundary} : {}),
        }

        const row = await performGQL({
            input
        }, createUserMortgageUploadStatementBalance);
        return row
    }

    const updateBalance = async (balanceID, _version, date, amount, balanceBoundary) => {
        const row = await performGQL({
            input: {
                id: balanceID,
                date,
                balance: Number(amount),
                ...(balanceBoundary ? {balanceBoundary} : {}),
                _version
            }
        }, updateUserMortgageUploadStatementBalance);
        return row
    }

    const deleteBalance = async (balanceID) => {
        let balance = upload.balances.items.find(b => b.id === balanceID);
        const row = await performGQL({
            input: {
                id: balanceID,
                _version: balance._version
            }
        }, deleteUserMortgageUploadStatementBalance);
        await mortgage.setMortgage(s => {
            const existingBalances = s.uploads.find(u => u.id === uploadID)?.balances?.items || [];
            return {
                ...s,
                uploads: s.uploads.map(u => {
                    if (u.id === uploadID) {
                        return {
                            ...u,
                            balances: {
                                items: existingBalances.filter(b => b.id !== balanceID)
                            }
                        }
                    }
                    return u;
                })
            }
        })
    }

    // Reads the statement's own PDF and fills in exactly the dates this
    // form still needs (additionalInitEntries below - the same placeholder
    // rows initialValues.entries already shows with an empty balance),
    // instead of the admin typing each one in by hand.
    //
    // extractStatementBalances is fire-and-forget now (invokeTask, not a
    // synchronous write) - a real PDF parse + S3 round trip can take a few
    // seconds, which the mutation resolver shouldn't block on. So this
    // mutation's own return value is just {uploadID, status: 'queued'},
    // never the actual balances - those arrive later, written directly via
    // GraphQL, and reach this form the same way any other admin's own
    // manual edit would: MortgageProvider.jsx's own onCreate/onUpdate
    // UserMortgageUploadStatementBalance subscription updates
    // mortgage.uploads[].balances.items, which the effect below syncs into
    // this form whenever it changes, whatever the source.
    const fillFromStatement = async () => {
        const datesNeeded = deduplicatedAdditionalInitEntries.map(e => ({date: e.date, boundary: e.balanceBoundary}));
        if (!datesNeeded.length) return;

        setExtracting(true);
        try {
            await performGQL({
                action: 'extractStatementBalances',
                payload: JSON.stringify({uploadID, dates: datesNeeded}),
            }, productAPIMutation);

            message.info('Reading the statement - balances will fill in automatically once ready');
        } catch (error) {
            console.error('extractStatementBalances failed', error);
            message.error(error.message || 'Could not read balances from the statement');
        } finally {
            setExtracting(false);
        }
    }

    // Keeps this form's own entries in sync with upload.balances.items
    // however it changes - the live subscription (fillFromStatement's own
    // eventual result, another admin's manual edit elsewhere, this tab's
    // own onFinish/deleteBalance) - antd's Form only applies its own
    // initialValues once, at mount, so a state update alone never reaches
    // already-rendered fields without this.
    useEffect(() => {
        const savedItems = upload?.balances?.items?.filter(b => !b._deleted) ?? [];
        if (!savedItems.length) return;

        // Matched on (date, boundary), not date alone - a manual entry's own
        // balanceBoundary is null/undefined, normalised the same way here so
        // it only ever matches another null/undefined-boundary row, never a
        // system-tagged OPENING/CLOSING one that happens to share its date.
        const boundaryKey = b => b.balanceBoundary || null;
        const currentEntries = form.getFieldValue('entries') || [];
        const updatedEntries = currentEntries.map(entry => {
            const match = savedItems.find(b => b.date === entry.date && boundaryKey(b) === boundaryKey(entry));
            return match ? {...entry, balance: match.balance, balanceBoundary: match.balanceBoundary || null, id: match.id} : entry;
        });
        const shown = new Set(updatedEntries.map(e => `${e.date}|${boundaryKey(e)}`));
        const newRows = savedItems
            .filter(b => !shown.has(`${b.date}|${boundaryKey(b)}`))
            .map(b => ({date: b.date, balance: b.balance, balanceBoundary: b.balanceBoundary || null, id: b.id}))

        if (newRows.length || JSON.stringify(updatedEntries) !== JSON.stringify(currentEntries)) {
            form.setFieldsValue({entries: [...updatedEntries, ...newRows].sort(balancesSorter)});
        }
    }, [upload?.balances?.items]);


    const onFinish = async (values) => {
        setBusy(true)

        // Handle form submission here, e.g., send data to the server or update state
        // You can also reset the form if needed
        // form.resetFields();
        const update = {
            target: `uploads[${uploadID}]`,
            pairs: {
                startDate: values.startDate,
                endDate: values.endDate
            }
        }

        const newBalances = [];

        values.entries.forEach(entry => {
            if (!entry.date || !entry.balance) {
                return;
            }
            if (entry.id) {
                let balance = upload.balances.items.find(b => b.id === entry.id);
                if (balance.date !== entry.date || balance.balance !== Number(entry.balance)) {
                    newBalances.push(updateBalance(balance.id, balance._version, entry.date, Number(entry.balance), entry.balanceBoundary));
                }
            } else {
                newBalances.push(addNewBalance(entry.date, entry.balance, entry.balanceBoundary));
            }
        })

        const editedBalances = await Promise.all(newBalances);
        if (editedBalances.length > 0) {
            mortgage.setMortgage(s => {
                const existingBalances = s.uploads.find(u => u.id === uploadID)?.balances?.items || [];
                editedBalances.forEach(row => {
                    let nExisting = existingBalances.find(b => b.id === row.id);
                    if (!nExisting) {
                        existingBalances.push(row);
                    } else {
                        nExisting.date = row.date;
                        nExisting.balance = row.balance;
                        nExisting.balanceBoundary = row.balanceBoundary;
                        nExisting._version = row._version;
                    }
                })

                return {
                    ...s,
                    uploads: s.uploads.map(u => {
                        if (u.id === uploadID) {
                            return {
                                ...u,
                                balances: {
                                    items: existingBalances
                                }
                            }
                        }
                        return u;
                    })
                }
            })
        }


        await mortgage.mutate({update})
        setSaved(true)
        setBusy(false)

    }


    initialValues.entries = [...(initialValues.entries||[]), ...deduplicatedAdditionalInitEntries].sort(balancesSorter)
    return (

        <ConfigProvider theme={{
            components: {
                Form: {
                    verticalLabelPadding: '0px'
                }
            }
        }}>
            <Form form={form} initialValues={initialValues} requiredMark={false} layout="vertical"
                  onValuesChange={onValuesChange}
                  onFinish={onFinish}>

                {processedAnalysisErrors.length > 0 && (
                    <Space direction="vertical" className="w-100 mb-15">
                        {processedAnalysisErrors.map(([key, message]) => (
                            <Alert key={key} type="error" showIcon message={key} description={message}/>
                        ))}
                    </Space>
                )}

                <Typography.Title level={5} className="m-0">Upload Range</Typography.Title>
                {/*<div className="d-row gap-3 ">*/}
                {/*    <Form.Item label="1st Transaction Date" className="mb-0" name="startDate"*/}
                {/*               rules={[{required: true, message: 'Please select a date!'}]}>*/}
                {/*        <Input type="date"/>*/}
                {/*    </Form.Item>*/}
                {/*    <Form.Item label="Date of Statement" className="mb-0" name="endDate"*/}
                {/*               rules={[{required: true, message: 'Please select a date!'}]}>*/}
                {/*        <Input type="date"/>*/}
                {/*    </Form.Item>*/}

                {/*</div>*/}
                {/*<div className="d-row gap-3 w-100 my-3">*/}
                {/*    <div style={{padding: 6, backgroundColor: gold.primary, borderRadius: 4}}>*/}
                {/*        <p className="m-0">Balances on a date to be at 12:00am</p>*/}
                {/*        <p className="m-0">Dont include transactions on the date of statement</p>*/}
                {/*    </div>*/}
                {/*</div>*/}
                <div className="d-row a-center gap-15 mb-15">
                    <Typography.Title level={5} className="m-0">Balances Record</Typography.Title>
                    {deduplicatedAdditionalInitEntries.length > 0 && (
                        <Button size="small" loading={extracting} onClick={fillFromStatement}>
                            Fill {deduplicatedAdditionalInitEntries.length} balance{deduplicatedAdditionalInitEntries.length > 1 ? 's' : ''} from statement
                        </Button>
                    )}
                </div>
                <Form.List name="entries">
                    {(fields, {add, remove}) => (
                        <>
                            {fields.map(({key, name, ...restField}) => (

                                <div className="d-row a-center gap-15 mb-15 grow" key={key}>
                                    <div className="d-row a-center gap-15 grow">
                                        <span style={{whiteSpace: "nowrap"}}>Balance</span>
                                        <Form.Item
                                            className="mb-0"
                                            {...restField}
                                            name={[name, 'date']}
                                            rules={[{required: true, message: 'Date required'}]}
                                        >
                                            <Input type={"date"}/>
                                        </Form.Item>

                                        <Form.Item
                                            className="mb-0"
                                            {...restField}
                                            name={[name, 'balance']}
                                            rules={[{required: true, message: 'Balance required'}]}
                                        >
                                            <Input type="number" step="0.01" placeholder="Balance"
                                                   onPaste={onPasteCurrencyHandler}/>
                                        </Form.Item>

                                        {/* Never user-editable - OPENING/CLOSING is decided once, by
                                            which system-computed date this row came from (see
                                            additionalInitEntries above), not something an admin sets
                                            by hand. Only a visual label + a hidden field so it
                                            round-trips on submit. */}
                                        <Form.Item noStyle shouldUpdate>
                                            {() => {
                                                const boundary = form.getFieldValue(['entries', name, 'balanceBoundary']);
                                                return boundary
                                                    ? <Tag color={boundary === 'OPENING' ? 'blue' : 'green'}>{boundary === 'OPENING' ? 'Opening' : 'Closing'}</Tag>
                                                    : null;
                                            }}
                                        </Form.Item>

                                        <Form.Item
                                            className="mb-0"
                                            {...restField}
                                            name={[name, 'balanceBoundary']}
                                        >
                                            <Input type="hidden"/>
                                        </Form.Item>

                                        <Form.Item
                                            className="mb-0"
                                            {...restField}
                                            name={[name, 'id']}
                                        >
                                            <Input type="hidden"/>
                                        </Form.Item>
                                    </div>

                                    <MinusCircleOutlined onClick={() => {
                                        // check if this row has an id
                                        const balanceID = form.getFieldValue(['entries', name, 'id']);
                                        if (balanceID) {
                                            deleteBalance(balanceID)
                                        }

                                        remove(name)
                                    }}/>
                                </div>
                            ))}

                            <Form.Item>
                                <Button
                                    type="dashed"
                                    onClick={() => add({id: null})}
                                    icon={<PlusOutlined/>}
                                >
                                    Add Balance on Date
                                </Button>
                            </Form.Item>
                        </>
                    )}
                </Form.List>

                <div className="d-row gap-3 w-100 j-end mt-3 a-center">
                    {saved && <span style={{color: green.primary}}><CheckCircleOutlined/> Saved</span>}
                    <Button htmlType="submit" danger={formChanged} icon={busy ? <LoadingOutlined/> : null}>Save</Button>
                </div>

            </Form>

        </ConfigProvider>
    );
}

export default BalancesForm;